import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Divider,
  Box,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import ParkIcon from '@mui/icons-material/Park';
import { useSelector } from 'react-redux';

import { RootState } from '../store/index';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

type MenuItemConfig = {
  text: string;
  icon: React.ReactElement;
  path?: string;
  badge: string | null;
  children?: Array<{ text: string; path: string }>;
};

const menuItems: MenuItemConfig[] = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    badge: null,
  },
];

export function Sidebar({ open, onClose }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  const collapsedWidth = 60;
  const expandedWidth = 280;
  const drawerWidth = isMobile ? expandedWidth : collapsedWidth;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f0f0f0',
      }}
    >
      <Box
        sx={{
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
        }}
      >
        <Avatar
          sx={{
            bgcolor: '#ffffff',
            color: 'primary.main',
            width: 34,
            height: 34,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <ParkIcon />
        </Avatar>
      </Box>

      <Divider />

      <List
        sx={{
          flexGrow: 1,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ width: 'auto' }}>
              <Tooltip title={item.text} placement="right" arrow>
                <ListItemButton
                  onClick={() => {
                    if (item.path) handleNavigation(item.path);
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    border: '1px solid #E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    position: 'relative',
                    transition: '0.2s ease',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                    ...(isActive
                      ? {
                          backgroundColor: 'primary.main',
                          borderColor: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.main' },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            right: -9,
                            transform: 'translateY(-50%)',
                            width: 0,
                            height: 0,
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderLeft: '5px solid',
                            borderLeftColor: 'primary.main',
                          },
                        }
                      : null),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      color: isActive ? '#ffffff' : '#707070',
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>


      <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title={user?.name || 'Usuário'} placement="right" arrow>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: '#ffffff',
              color: 'primary.main',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          backgroundColor: '#f0f0f0',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
